import agentWebSocket, { type ConnectionState } from "@app/config/agentWebSocket";
import { queryClient } from "@app/config/api/api";
import { agentSessionMessagesOptions, translateBackendMessages, useAgentSessionMessages } from "@app/queries/agent";
import { useAuth0 } from "@auth0/auth0-react";
import type { AgentServerMessage, ExperimentId } from "@globals";
import type { ChatMessage } from "@lib/agent/types";
import { useCallback, useEffect, useRef, useState } from "react";

let messageIdCounter = 0;
function nextId(): string {
  return `msg-${++messageIdCounter}`;
}

export function useAgentChat(experimentId: ExperimentId, teamId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(agentWebSocket.state);
  const isReady = connectionState === "connected";
  const streamBufferRef = useRef("");
  const currentAssistantIdRef = useRef<string | null>(null);
  const toolCallsRef = useRef<string[]>([]);
  // Seeding the local message list happens once. Subsequent refetches (e.g. window-focus)
  // must not clobber turns that have streamed in since.
  const hasSeededRef = useRef(false);
  // Set to true when agent.response.done is received for a live turn. Prevents
  // agent.history.updated (broadcast to all consumers, including the live one) from
  // reopening the seed gate and clobbering extras that response.done just attached.
  const hadLiveTurnRef = useRef(false);
  const experimentIdRef = useRef(experimentId);
  useEffect(() => {
    experimentIdRef.current = experimentId;
  }, [experimentId]);

  const { getAccessTokenSilently } = useAuth0();

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: historyError,
    refetch: refetchHistory
  } = useAgentSessionMessages(experimentId);

  // Seed local state from persisted history exactly once.
  //
  // Skip the setMessages call when history is empty. Effect order is child-first within a
  // commit, so on a fresh draft entered from `?query=...` ExperimentAgentChat's handoff
  // effect dispatches `send()` (which appends the user + pending-assistant messages to
  // local state) BEFORE this parent effect fires. A blanket `setMessages([...])` would
  // then overwrite that local state with the empty server history and the typed message
  // would vanish. Empty history has nothing to restore anyway.
  //
  // If the server signals `is_turn_in_progress`, a turn survived a previous WS disconnect
  // and is still running. Show the same "thinking" spinner as during live streaming so
  // the user is not left wondering why the chat appears frozen.
  useEffect(() => {
    if (hasSeededRef.current || !historyData) {
      return;
    }
    hasSeededRef.current = true;
    const seeded = historyData.messages.length > 0 ? translateBackendMessages(historyData.messages) : null;
    if (historyData.is_turn_in_progress) {
      const pendingId = nextId();
      // Do NOT set currentAssistantIdRef here: the server owns this turn, not the
      // client. Keeping it null lets the reconnect guard reopen the seed gate on the
      // next WS reconnect so the history refetch can replace this spinner bubble
      // once the turn finishes — without conflating it with a locally-owned live stream.
      setMessages([...(seeded ?? []), { id: pendingId, role: "assistant", content: "" }]);
      setIsStreaming(true);
      return;
    }
    // Turn finished while the WS was down. Clear any stale streaming state that
    // was set by a prior seed (e.g. reconnect after a detached turn completed).
    // Guard on currentAssistantIdRef: if it is set, a live send() owns the stream
    // and we must not clobber it (e.g. draft ?query= handoff where the refetch of
    // empty history races the in-flight prompt).
    if (!currentAssistantIdRef.current) {
      setIsStreaming(false);
      streamBufferRef.current = "";
      toolCallsRef.current = [];
      // Always write messages when no live send() is in flight. When seeded is
      // null (server returned 0 messages) this clears any stale placeholder bubble
      // the prior detached-turn seed injected into local state.
      setMessages(seeded ?? []);
    }
  }, [historyData]);

  const clearLocal = useCallback(() => {
    setMessages([]);
    streamBufferRef.current = "";
    currentAssistantIdRef.current = null;
    toolCallsRef.current = [];
    setIsStreaming(false);
  }, []);

  // Re-open the seed gate so the next `historyData` change rehydrates messages from the
  // server. Used by the optimistic-clear rollback path: after `clearLocal()` empties the
  // UI and the DELETE fails, the consumer calls this + `refetchHistory()` to recover.
  // We don't reset the gate inside `clearLocal` itself because on the success path that
  // would race a user typing a new message against the post-invalidate refetch.
  const allowReseed = useCallback(() => {
    hasSeededRef.current = false;
  }, []);

  // Connect on mount, reconnect when the experiment's team changes
  useEffect(() => {
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
    return () => agentWebSocket.close();
  }, [teamId, getAccessTokenSilently]);

  // Track connection state
  useEffect(() => agentWebSocket.onStateChange(setConnectionState), []);

  // On (re)connect, invalidate history so a turn that completed while the WS was down is
  // picked up immediately — covers the case where the agent finished before this client
  // connected and `is_turn_in_progress` was already false on the initial fetch.
  // Reset the seed gate so the refetch actually updates local state: if the initial load
  // saw `is_turn_in_progress: true` and the turn finished before this reconnect refetch
  // returns, the seeding effect would otherwise bail out and leave the empty streaming
  // bubble in place.
  useEffect(() => {
    if (!isReady) {
      return;
    }
    // Only reopen the seed gate when idle. If the user is mid-stream (optimistic
    // user message + in-flight assistant bubble), the refetch must not clobber
    // local state that has not yet been persisted to the server.
    // currentAssistantIdRef is only set by a locally-owned send() — the detached-turn
    // seed path intentionally leaves it null — so this guard correctly stays open
    // for detached turns and closed only for live client-initiated streams.
    if (!currentAssistantIdRef.current) {
      hasSeededRef.current = false;
    }
    void queryClient.invalidateQueries({
      queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
    });
  }, [isReady]);

  // Handle server messages
  useEffect(() => {
    const handleMessage = (message: AgentServerMessage) => {
      switch (message.action) {
        case "agent.response.chunk": {
          streamBufferRef.current += message.payload.message;
          const buffered = streamBufferRef.current;
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, content: buffered } : m)));
          }
          break;
        }
        case "agent.tool_use": {
          toolCallsRef.current = [...toolCallsRef.current, message.payload.tool];
          const tools = toolCallsRef.current;
          // Insert a paragraph break so text that resumes after the tool call
          // renders as a separate markdown paragraph from the pre-tool text.
          if (streamBufferRef.current && !streamBufferRef.current.endsWith("\n\n")) {
            streamBufferRef.current += "\n\n";
          }
          const buffered = streamBufferRef.current;
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev =>
              prev.map(m => (m.id === assistantId ? { ...m, content: buffered, toolCalls: tools } : m))
            );
          }
          break;
        }
        case "agent.response.done": {
          // Capture before clearing: stop() sets this to null synchronously before the
          // server's response.done arrives, so a null here means stop/cancel rather than
          // a normal completion.
          const assistantId = currentAssistantIdRef.current;
          if (message.payload?.extras && assistantId) {
            const extras = message.payload.extras;
            setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, extras } : m)));
          }
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          // Only mark a live completion: stop/cancel clears currentAssistantIdRef before
          // this handler fires and is never followed by agent.history.updated, so we must
          // not set the flag in that case or a later detached-turn history.updated will
          // incorrectly skip reopening the seed gate.
          if (assistantId) {
            hadLiveTurnRef.current = true;
          }
          // The agent service has now persisted the AgentSession row and committed messages —
          // refresh so `agent_session_id` surfaces and the committed message count signal updates.
          void queryClient.invalidateQueries({
            queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
          });
          break;
        }
        case "agent.error": {
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev =>
              prev.map(m => (m.id === assistantId ? { ...m, content: m.content || message.payload.message } : m))
            );
          } else {
            setMessages(prev => [...prev, { id: nextId(), role: "assistant", content: message.payload.message }]);
          }
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          break;
        }
        case "agent.history.updated": {
          // A detached turn (started before a WS disconnect) has just completed. Discard
          // any partial streaming state and refetch the full, persisted conversation.
          if (message.payload.experiment_id !== experimentIdRef.current) {
            break;
          }
          if (!hadLiveTurnRef.current) {
            // Only reopen the seed gate for detached turns (WS was disconnected during the
            // turn). For live turns the gate stays closed so the extras set by
            // agent.response.done are not clobbered when the refetch resolves.
            hasSeededRef.current = false;
          }
          hadLiveTurnRef.current = false;
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          void queryClient.invalidateQueries({
            queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
          });
          break;
        }
      }
    };

    return agentWebSocket.onServerMessage(handleMessage);
  }, []);

  const send = useCallback(
    (content: string): void => {
      if (!content.trim() || isStreaming || isHistoryLoading || historyError) {
        return;
      }
      // The input is gated on `isReady` at the component layer; this guard catches the
      // race where the WS drops between render and send so the frame isn't silently
      // discarded on a dead socket. The user retries once the auto-reconnect catches up.
      if (!isReady) {
        return;
      }

      const userMessage: ChatMessage = { id: nextId(), role: "user", content };
      const assistantMessage: ChatMessage = { id: nextId(), role: "assistant", content: "" };
      currentAssistantIdRef.current = assistantMessage.id;
      streamBufferRef.current = "";
      toolCallsRef.current = [];

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      agentWebSocket.send({
        action: "agent.prompt",
        payload: { message: content, experiment_id: experimentId }
      });
    },
    [isStreaming, isReady, isHistoryLoading, historyError, experimentId]
  );

  const stop = useCallback(() => {
    if (!isStreaming) {
      return;
    }
    agentWebSocket.send({
      action: "agent.stop",
      payload: { experiment_id: experimentId }
    });
    // Mirror the backend cleanup locally so the partial assistant bubble disappears
    // immediately — the consumer deletes the same row server-side, so without this the
    // UI would keep showing the half-written reply until a reload refetched history.
    // The user message is preserved on both sides so the user can retry without retyping.
    const assistantId = currentAssistantIdRef.current;
    if (assistantId) {
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    }
    // Clear immediately so the incoming agent.response.done (sent by the stop handler)
    // is treated as a cancel completion, not a live turn — preventing hadLiveTurnRef
    // from being set when no agent.history.updated will follow.
    currentAssistantIdRef.current = null;
  }, [isStreaming, experimentId]);

  const agentSessionId = historyData?.agent_session_id ?? null;

  return {
    messages,
    isStreaming,
    connectionState,
    send,
    stop,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    allowReseed,
    // Derived from `historyData` (not from local `messages`) so callers gating on "is
    // there persisted history?" can rely on it during the same render the seed effect
    // commits — the local `messages` state lags by one render due to setState batching.
    // Uses the filtered translation so agent-only `[SYSTEM]` rows can't make a visually
    // empty chat count as restored history (e.g. suppressing the initial-query handoff).
    hasRestoredHistory: translateBackendMessages(historyData?.messages ?? []).length > 0,
    agentSessionId,
    committedMessageCount: historyData?.messages?.length ?? 0
  };
}
